import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { unreachable } from '@libs/unreachable';
import { environment } from '@apps/clos-sessions/environments';
import { ClosArchiveBucket, ClosAsset } from './types';
import {
  BinaryDetailsDTO,
  ClosAssetDirectory,
  ClosAssetDTO,
  ClosAssetRegisterDTO,
  ClosAssetsRecordDTO,
  UploadIntentionDTO,
} from './types.dto';

@Injectable({
  providedIn: 'root',
})
export class ArchivesAssetsApi {
  private baseUrl = `${environment.api.baseUrl}`;

  constructor(protected http: HttpClient) {}

  getAssets(input: GetAssetsInput): Observable<GetAssetsOutput> {
    return this.http
      .get<{ images: ClosAssetDTO[] }>(
        `${this.baseUrl}/v1/room/${encodeURIComponent(input.archive.id)}/images`,
        {
          params: {
            retouch: `${input.bucket === ClosArchiveBucket.sharing}`,
            limit: `${input.take}`,
            skip: `${input.skip}`,
          },
        },
      )
      .pipe(
        switchMap(({ images }) => {
          const list = images.map((assetDto) => this.toClosArchiveAsset(assetDto, input.archive));

          if (list.length < input.take) {
            return of({
              hasNextChunk: false,
              list,
            });
          }

          return this.getPaginationChunkState({
            ...input,
            skip: input.skip + input.take,
          }).pipe(
            map(({ hasNextChunk }) => ({
              hasNextChunk,
              list,
            })),
          );
        }),
      );
  }

  getAssetById(input: GetAssetInput): Observable<ClosAsset> {
    const archiveId = encodeURIComponent(input.archive.id);
    const assetId = encodeURIComponent(input.assetId);

    return this.http
      .get<{
        data: ClosAssetDTO;
      }>(`${this.baseUrl}/v1/room/${archiveId}/images/${assetId}`)
      .pipe(map(({ data }) => this.toClosArchiveAsset(data, input.archive)));
  }

  findAssetByName(
    archive: { id: string },
    bucket: ClosArchiveBucket,
    name: string,
    skip = 0,
  ): Observable<ClosAsset> {
    const limit = 100;
    return this.http
      .get<{
        images: ClosAssetDTO[];
      }>(`${this.baseUrl}/v1/room/${encodeURIComponent(archive.id)}/images`, {
        params: { retouch: `${bucket === ClosArchiveBucket.sharing}`, limit, skip },
      })
      .pipe(
        switchMap(({ images }) => {
          const asset = images.find((image) => image.name === name);
          if (asset) {
            return of(this.toClosArchiveAsset(asset, archive));
          }
          if (images.length === limit) {
            return this.findAssetByName(archive, bucket, name, skip + limit);
          }
          return throwError(() => new ClosAssetNameNotFoundError(name));
        }),
      );
  }

  removeAssets({ assets, archive }: RemoveAssetsInput): Observable<unknown> {
    return this.http.request(
      'delete',
      `${this.baseUrl}/v1/room/${encodeURIComponent(archive.id)}/images`,
      {
        body: {
          ids: assets.map(({ name }) => name),
        },
      },
    );
  }

  rateAsset({ score, asset, archive }: RateAssetInput): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.baseUrl}/v1/room/${encodeURIComponent(archive.id)}/images/rating`,
      {
        image_name: asset.name,
        score,
      },
    );
  }

  registerAssets(input: RegisterAssetsInput): Observable<RegisterAssetsOutput> {
    const { archive, bucket, assetBinaryDetails } = input;
    const payload: ClosAssetRegisterDTO[] = assetBinaryDetails.map(({ name, size, type }) => ({
      name,
      size,
      type,
      retouch: bucket === ClosArchiveBucket.sharing,
    }));

    return this.http
      .post<ClosAssetsRecordDTO>(
        `${this.baseUrl}/v1/room/${encodeURIComponent(archive.id)}/images`,
        payload,
      )
      .pipe(
        map((record) => {
          return assetBinaryDetails.reduce<RegisterAssetsOutput>(
            (acc, item) => ({
              ...acc,
              [item.id]: record.list[item.name],
            }),
            {},
          );
        }),
      );
  }

  declareUploadIntention(input: DeclareUploadIntentionInput): Observable<UploadIntentionDTO> {
    return this.http.post<UploadIntentionDTO>(`${this.baseUrl}/v1/image/upload`, null, {
      params: {
        room_id: input.archive.id,
        type: this.getDirectoryByBucket(input.bucket),
        image_size: input.binaryDetails.size,
        image_name: input.binaryDetails.name,
      },
    });
  }

  confirmAssetUploadingCompleteness(
    input: ConfirmAssetUploadingInput,
  ): Observable<ConfirmAssetUploadingOutput> {
    return this.http.post<ConfirmAssetUploadingOutput>(`${this.baseUrl}/v1/image/complete`, {
      room_id: input.archive.id,
      image_name: input.binaryDetails.name,
      type: this.getDirectoryByBucket(input.bucket),
    });
  }

  protected getPaginationChunkState(
    input: Omit<GetAssetsInput, 'take'>,
  ): Observable<Pick<GetAssetsOutput, 'hasNextChunk'>> {
    return this.http
      .get<{ images: ClosAssetDTO[] }>(
        `${this.baseUrl}/${encodeURIComponent(input.archive.id)}/images`,
        {
          params: {
            limit: 1,
            skip: input.skip,
          },
        },
      )
      .pipe(
        map(({ images }) => ({
          hasNextChunk: images.length > 0,
        })),
      );
  }

  protected toClosArchiveAsset(dto: ClosAssetDTO, archive: { id: string }): ClosAsset {
    const url = dto.presigned_url || dto.presigned_preview_url || '';
    const date = dto.created_at || dto.reserved_at || dto.uploaded_at || '1970-01-01T00:00:00.000Z';

    return {
      id: dto._id,
      name: dto.name,
      url,
      previewUrl: dto.presigned_preview_url || url,
      rate: dto.score,
      archiveId: archive.id,
      bucket: dto.retouch ? ClosArchiveBucket.sharing : ClosArchiveBucket.session,
      createdAt: {
        date,
        timestamp: new Date(date).getTime(),
      },
      dto: {
        ...dto,
        presigned_preview_url: dto.presigned_preview_url || url,
      },
    };
  }

  protected getDirectoryByBucket(bucket: ClosArchiveBucket): ClosAssetDirectory {
    switch (bucket) {
      case ClosArchiveBucket.sharing:
        return ClosAssetDirectory.sharing;
      case ClosArchiveBucket.session:
        return ClosAssetDirectory.sessions;
      default:
        return unreachable(bucket);
    }
  }
}

/** The bucket has no file with this name. The name may still be taken by the other bucket. */
export class ClosAssetNameNotFoundError extends Error {
  constructor(readonly fileName: string) {
    super('Registered upload was not found in the room');
    this.name = 'ClosAssetNameNotFoundError';
  }
}

export interface GetAssetsInput {
  archive: { id: string };
  bucket: ClosArchiveBucket;
  take: number;
  skip: number;
}

export interface GetAssetInput {
  archive: { id: string };
  assetId: string;
}

export interface GetAssetsOutput {
  list: ClosAsset[];
  hasNextChunk: boolean;
}

export interface RemoveAssetsInput {
  archive: { id: string };
  assets: ClosAsset[];
}

export interface RateAssetInput {
  archive: { id: string };
  asset: ClosAsset;
  score: number;
}

export interface RegisterAssetsInput {
  archive: { id: string };

  bucket: ClosArchiveBucket;

  assetBinaryDetails: Array<BinaryDetailsDTO & { id: ClosAsset['guid'] }>;
}

export type RegisterAssetsOutput = Record<ClosAsset['guid'], ClosAsset['id']>;

export interface DeclareUploadIntentionInput {
  archive: { id: string };

  bucket: ClosArchiveBucket;

  binaryDetails: BinaryDetailsDTO;
}

export interface ConfirmAssetUploadingInput {
  archive: { id: string };

  bucket: ClosArchiveBucket;

  binaryDetails: BinaryDetailsDTO;
}

export interface ConfirmAssetUploadingOutput {
  success: boolean;
}
